import * as yup from 'yup';
import { RendererType } from '../data-models/field-definition';
import { percentField, usdField } from './common-schema-defs';
import { FundDataModel, fundFields, fundProfilesFormSchema } from './Fund.schema';

export enum FundViewModelCalcType {
  total = 'Total',
  lpOnly = 'LP Only',
}

export const PreferredReturnType = {
  hurdle: 'Hurdle',
  threshold: 'Threshold',
  thresholdIncomeType: 'Threshold (NII vs NI)',
} as const;
export type PreferredReturnType = (typeof PreferredReturnType)[keyof typeof PreferredReturnType];

export function fundViewModelFields() {
  return {
    tier1: yup
      .string()
      .oneOf(Object.values(PreferredReturnType))
      .nullable()
      .label('Type')
      .default(PreferredReturnType.threshold)
      .required()
      .formMeta({
        renderer: RendererType.singleSelect,
        rendererMeta: {
          values: Object.values(PreferredReturnType).map((value) => ({
            displayName: value,
            value,
          })),
        },
        disableClearable: true,
      }),
    totalAllocationAmount: usdField().nullable().default(null).label(''),
    totalAllocationPercentage: percentField().nullable().default(null).label('Total Allocation'),
  };
}

function fundViewModelBase() {
  return yup.object(fundViewModelFields());
}

export function fundViewModelSchema() {
  const {
    lpGpSplit,
    lpGpSplitThreshold,
    secondaryReturnHurdle,
    secondaryReturnSplit,
    gpCatchUpPercentage,
    incomeType,
    preferredReturnHurdle,
    premiumCatchupPercentage,
    premiumCatchupThreshold,
    superReturnSplit,
  } = fundFields();
  return fundProfilesFormSchema().shape({
    _viewModel: fundViewModelBase(),
    incomeType: incomeType.when('_viewModel.tier1', {
      is: PreferredReturnType.thresholdIncomeType,
      then: (schema) => schema.required(),
    }),
    lpGpSplit: lpGpSplit.when('_viewModel.tier1', {
      is: PreferredReturnType.threshold,
      then: (schema) => schema.required(),
    }),
    lpGpSplitThreshold: lpGpSplitThreshold.when('_viewModel.tier1', {
      is: PreferredReturnType.threshold,
      then: (schema) => schema.required(),
    }),
    preferredReturnHurdle: preferredReturnHurdle.when('_viewModel.tier1', {
      is: PreferredReturnType.hurdle,
      then: (schema) => schema.required(),
    }),
    secondaryReturnHurdle: secondaryReturnHurdle.when('isSecondaryReturnEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
    secondaryReturnSplit: secondaryReturnSplit.when('isSecondaryReturnEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
    gpCatchUpPercentage: gpCatchUpPercentage.when('enableGPCatchup', {
      is: true,
      then: (schema) => schema.required(),
    }),
    premiumCatchupPercentage: premiumCatchupPercentage.when('isPremiumCatchupEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
    premiumCatchupThreshold: premiumCatchupThreshold.when('isPremiumCatchupEnabled', {
      is: true,
      then: (schema) => schema.required(),
    }),
    superReturnSplit: superReturnSplit.when('enableSuperReturn', {
      is: true,
      then: (schema) => schema.required(),
    }),
  });
}

export type FundViewModel = Omit<
  yup.InferType<ReturnType<typeof fundViewModelSchema>>,
  | 'followOnInvestingWindowCloseDate'
  | 'initialInvestingWindowCloseDate'
  | 'investingWindowInceptionDate'
  | 'managementFeeTerminationDate'
> & {
  followOnInvestingWindowCloseDate?: string | null;
  initialInvestingWindowCloseDate?: string | null;
  investingWindowInceptionDate?: string | null;
  managementFeeTerminationDate?: string | null;
};

export function fromFundViewModel(fundViewModel: FundViewModel): FundDataModel {
  const { _viewModel, ...rest } = fundViewModel;
  const result = { ...rest };

  // reset fields that make sense only for one tier1 type
  if (_viewModel.tier1 === PreferredReturnType.hurdle) {
    result.isTimeWeighted = true;
    result.lpGpSplit = null;
    result.lpGpSplitThreshold = null;
    result.incomeType = null;
  } else if (_viewModel.tier1 === PreferredReturnType.thresholdIncomeType) {
    result.isTimeWeighted = false;
    result.preferredReturnHurdle = null;
  } else if (_viewModel.tier1 === PreferredReturnType.threshold) {
    result.isTimeWeighted = false;
    result.preferredReturnHurdle = null;
    result.incomeType = null;
  }

  if (!fundViewModel.isSecondaryReturnEnabled) {
    result.secondaryReturnHurdle = null;
    result.secondaryReturnSplit = null;
  }
  if (!fundViewModel.enableGPCatchup) {
    result.gpCatchUpPercentage = null;
  }
  if (!fundViewModel.isPremiumCatchupEnabled) {
    result.premiumCatchupPercentage = null;
    result.premiumCatchupThreshold = null;
  }
  //  if (!fundViewModel.enableSuperReturn) { MAGGIE-9858
  //    result.superReturnSplit = null;
  //  }

  return result;
}

export function toFundViewModel(fund: FundDataModel): FundViewModel {
  const totalAllocationPercentage =
    (fund.initialInvestmentAllocationPercentage ?? 0) +
    (fund.feesAndExpensesAllocationPercentage ?? 0) +
    (fund.reservesAllocationPercentage ?? 0);

  return {
    ...fund,
    _viewModel: {
      tier1: getTier1(fund),
      totalAllocationAmount: (totalAllocationPercentage / 100) * (fund.committedCapital ?? 0),
      totalAllocationPercentage,
    },
  };
}

export function fundCapitalAllocationSchemaStep1() {
  const {
    committedCapital,
    feesAndExpensesAllocationPercentage,
    feesAndExpensesAllocationAmount,
    initialInvestmentAllocationPercentage,
    initialInvestmentAllocationAmount,
    recyclingLimitPercentage,
    recyclingLimitAmount,
    reservesAllocationPercentage,
    reservesAllocationAmount,
  } = fundFields();
  const { totalAllocationAmount, totalAllocationPercentage } = fundViewModelFields();

  return yup.object({
    committedCapital: committedCapital.required().min(10_000_000).max(100_000_000_000),
    feesAndExpensesAllocationPercentage: feesAndExpensesAllocationPercentage.required().min(0),
    feesAndExpensesAllocationAmount: feesAndExpensesAllocationAmount,
    initialInvestmentAllocationPercentage: initialInvestmentAllocationPercentage.required().min(0),
    initialInvestmentAllocationAmount,
    recyclingLimitPercentage: recyclingLimitPercentage.required().min(0),
    recyclingLimitAmount,
    reservesAllocationPercentage: reservesAllocationPercentage.required().min(0),
    reservesAllocationAmount,
    _viewModel: yup.object({
      totalAllocationAmount,
      totalAllocationPercentage,
    }),
  });
}

export function fundCapitalAllocationSchemaStep2() {
  const {
    investingWindowInceptionDate,
    initialInvestingWindowCloseDate,
    followOnInvestingWindowCloseDate,
    managementFeeTerminationDate,
  } = fundFields();

  return yup.object({
    investingWindowInceptionDate: investingWindowInceptionDate.required(),
    initialInvestingWindowCloseDate,
    followOnInvestingWindowCloseDate,
    managementFeeTerminationDate: managementFeeTerminationDate.required(),
  });
}

function getTier1(fund: FundDataModel) {
  if (fund.isTimeWeighted) {
    return PreferredReturnType.hurdle;
  } else if (fund.incomeType != null) {
    return PreferredReturnType.thresholdIncomeType;
  } else {
    return PreferredReturnType.threshold;
  }
}
